import React from 'react';
import { ArrowDown, Sparkles, Shield, Zap } from 'lucide-react';
import { LOGO_URL } from '../lib/format';
import { usePalmentWallet } from '../context/DemoWalletContext';
import DemoBadge, { DemoDisclaimer } from './ui/DemoBadge';

export default function Hero() {
  const { connected, openRealWallet, mode } = usePalmentWallet();
  const isDemo = mode === 'demo';

  const onEnter = () => {
    if (!connected) openRealWallet();
    const el = document.getElementById('vault');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Protocol metrics are still a simulation — we only show live-ish numbers
  // when the visitor is explicitly in demo mode. Otherwise render $0 / —%.
  const heroStats = isDemo
    ? [
        { v: '$8.42M', l: 'Total Value Locked' },
        { v: '12.4%', l: 'Current APY' },
        { v: '2,318', l: 'Active depositors' },
      ]
    : [
        { v: '$0', l: 'Total Value Locked' },
        { v: '—%', l: 'Current APY' },
        { v: '—', l: 'Active depositors' },
      ];

  return (
    <section id="top" className="relative overflow-hidden pt-14 pb-24 md:pt-24 md:pb-32">
      {/* Ambient orbs */}
      <div
        aria-hidden
        className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full blur-[140px] opacity-50 animate-pulseSlow"
        style={{ background: 'radial-gradient(circle, #076653 0%, transparent 65%)' }}
      />
      <div
        aria-hidden
        className="absolute top-10 right-[-10%] w-[420px] h-[420px] rounded-full blur-[120px] opacity-30 animate-pulseSlow"
        style={{ background: 'radial-gradient(circle, #E3EF26 0%, transparent 60%)' }}
      />
      <div aria-hidden className="absolute inset-0 grid-fade opacity-70" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-riseIn">
            <div
              className="inline-flex items-center gap-2 rounded-full glass px-3.5 py-1.5 text-[11px] tracking-[0.22em] uppercase text-palm-mint/90"
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-palm-lime animate-pulseSlow" />
              Solana · Devnet · Censorship-Resistant
            </div>

            <h1
              className="mt-6 font-display font-black tracking-[-0.03em] text-[44px] leading-[1.02] sm:text-6xl md:text-[72px]"
              data-testid="hero-title"
            >
              The yield vault
              <br />
              that never{' '}
              <span className="text-gradient">sleeps.</span>
            </h1>

            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-white/70" data-testid="hero-subtitle">
              Deposit <span className="text-palm-mint font-semibold">PUSD</span>, receive{' '}
              <span className="text-palm-lime font-semibold">yPUSD</span> — a yield-bearing,
              permissionless receipt token that compounds on-chain. Built for the unstoppable economy on Solana.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onEnter}
                className="group inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-palm-lime text-palm-surface font-bold tracking-wide shadow-lime hover:bg-palm-limeHover transition-all hover:-translate-y-0.5"
                data-testid="hero-enter-vault"
              >
                Enter the Vault
                <ArrowDown
                  size={18}
                  strokeWidth={2.2}
                  className="group-hover:translate-y-0.5 transition-transform"
                />
              </button>
              <a
                href="#how"
                className="inline-flex items-center gap-2 h-12 px-5 rounded-xl glass text-white/90 hover:border-palm-mint/30 transition-all"
                data-testid="hero-learn-more"
              >
                How it works
              </a>
            </div>

            <div className="mt-10 max-w-lg" data-testid="hero-stats">
              <div className="grid grid-cols-3 gap-4">
                {heroStats.map((s) => (
                  <div key={s.l} className="glass rounded-2xl px-4 py-3 relative">
                    <div className="flex items-center justify-between gap-1">
                      <div className="font-display font-bold text-lg text-white">{s.v}</div>
                      {isDemo && <DemoBadge />}
                    </div>
                    <div className="text-[11px] tracking-[0.14em] uppercase text-palm-mint/60 mt-1">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
              <DemoDisclaimer className="mt-4" />
            </div>
          </div>

          {/* Right side: animated coin */}
          <div className="lg:col-span-5">
            <CoinArt />
          </div>
        </div>

        {/* Feature strip */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-4">
          <FeatureChip
            icon={<Shield size={18} strokeWidth={1.6} />}
            title="Non-custodial by design"
            body="Your keys, your yield. Vault logic runs on immutable Solana programs."
          />
          <FeatureChip
            icon={<Zap size={18} strokeWidth={1.6} />}
            title="Sub-second settlement"
            body="Engineered for high-performance RPC — deposits land in one atomic transaction."
          />
          <FeatureChip
            icon={<Sparkles size={18} strokeWidth={1.6} />}
            title="Receipt tokens"
            body="yPUSD is composable across Solana DeFi. Trade, collateralize, or just hold."
          />
        </div>
      </div>
    </section>
  );
}

function FeatureChip({ icon, title, body }) {
  return (
    <div className="glass rounded-2xl p-5 flex gap-4 hover:border-palm-mint/25 transition-all">
      <div className="shrink-0 w-10 h-10 rounded-xl bg-palm-surface/60 border border-palm-mint/10 flex items-center justify-center text-palm-lime">
        {icon}
      </div>
      <div>
        <div className="font-display font-semibold tracking-tight text-white/95">{title}</div>
        <div className="mt-1 text-sm text-white/60 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function CoinArt() {
  return (
    <div className="relative mx-auto max-w-md aspect-square">
      {/* Outer gradient halo */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-full opacity-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle at 30% 30%, rgba(227,239,38,0.45), transparent 55%), radial-gradient(circle at 70% 70%, rgba(7,102,83,0.8), transparent 55%)',
        }}
      />
      {/* Orbit rings */}
      <div
        aria-hidden
        className="absolute inset-6 rounded-full border border-palm-mint/10"
        style={{ transform: 'rotate(18deg)' }}
      />
      <div
        aria-hidden
        className="absolute inset-14 rounded-full border border-palm-mint/10 border-dashed animate-[spin_40s_linear_infinite]"
      />
      {/* Coin */}
      <div className="absolute inset-10 md:inset-12 rounded-full glass-strong animate-floaty flex items-center justify-center">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'radial-gradient(circle at 30% 25%, rgba(226,251,206,0.35), transparent 45%), linear-gradient(135deg, rgba(7,102,83,0.6) 0%, rgba(12,52,44,0.8) 100%)',
          }}
        />
        <div className="absolute inset-4 rounded-full border border-palm-lime/30" />
        <div className="absolute inset-8 rounded-full border border-palm-mint/20" />
        <div className="relative z-10 flex flex-col items-center justify-center text-center">
          <img src={LOGO_URL} alt="PALMENT" className="w-28 h-28 md:w-36 md:h-36 drop-shadow-[0_0_30px_rgba(227,239,38,0.4)]" />
          <div className="mt-3 font-display font-black text-xl tracking-[0.22em] text-white">
            yPUSD
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-palm-mint/70 mt-1">
            Yield-Bearing PUSD
          </div>
        </div>
      </div>
      {/* Floating satellite chips */}
      <SatelliteChip label="APY 12.4%" className="top-2 right-6 animate-floaty [animation-delay:-2s]" />
      <SatelliteChip label="1 PUSD → 0.985 yPUSD" className="bottom-6 left-2 animate-floaty [animation-delay:-4s]" />
      <SatelliteChip label="Devnet" className="bottom-10 right-4 animate-floaty" />
    </div>
  );
}

function SatelliteChip({ label, className = '' }) {
  return (
    <div
      className={`absolute glass rounded-full px-3 py-1.5 text-[11px] tracking-widest uppercase text-palm-mint/90 ${className}`}
    >
      {label}
    </div>
  );
}
